import type { Action, Agent, Observation, ResourceView, Vec2 } from "@cc/protocol";
import { distance } from "@cc/protocol";
import { ARENA, TRAP_BAIT_VALUE } from "@cc/engine";

/**
 * Baseline bots.
 *
 * These exist for three reasons:
 *  1. they let the engine be developed and tested with zero API cost;
 *  2. they are the fallback when a model returns garbage or times out;
 *  3. they are the calibration line — an LLM that cannot beat `collector`
 *     is not interesting, and one that reliably beats `hunter` is.
 *
 * They share one parameterised implementation so tuning stays in one place.
 */
export interface BotProfile {
  readonly id: string;
  readonly persona: string;
  /** Weight on "grab the nearest valuable thing". */
  readonly greed: number;
  /** Weight on "close distance and attack". */
  readonly ferocity: number;
  /** Weight on scouting / trap avoidance. */
  readonly caution: number;
  /** HP below which the bot prefers to disengage. */
  readonly retreatHp: number;
}

export const BOT_PROFILES = {
  collector: {
    id: "collector",
    persona: "THE COLLECTOR",
    greed: 1.0,
    ferocity: 0.05,
    caution: 0.15,
    retreatHp: 40,
  },
  hunter: {
    id: "hunter",
    persona: "THE HUNTER",
    greed: 0.35,
    ferocity: 0.95,
    caution: 0.1,
    retreatHp: 25,
  },
  scout: {
    id: "scout",
    persona: "THE SCOUT",
    greed: 0.55,
    ferocity: 0.3,
    caution: 0.9,
    retreatHp: 55,
  },
} as const satisfies Record<string, BotProfile>;

function bestResource(
  obs: Observation,
  weight: (r: ResourceView) => number,
): ResourceView | null {
  let best: ResourceView | null = null;
  let bestScore = -Infinity;
  for (const r of obs.resources) {
    if (r.identifiedAsTrap) continue;
    const score = weight(r) - r.distance;
    if (score > bestScore) {
      bestScore = score;
      best = r;
    }
  }
  return best;
}

export function createBotAgent(profile: BotProfile): Agent {
  return {
    id: profile.id,
    persona: profile.persona,
    model: "heuristic",
    decide(obs: Observation): Action {
      const self = obs.self;
      const opp = obs.opponent;
      const arena = obs.arenaSize;
      const center: Vec2 = [arena / 2, arena / 2];
      const lowHp = self.hp <= profile.retreatHp;

      // 1. Survival: FLEE when critical and the enemy is pressing.
      if (self.hp <= profile.retreatHp * 0.55 && opp.visible && opp.position) {
        return { action: "FLEE", reason: "hp critical, disengaging" };
      }

      // 2. Spend inventory when a fight is live — bombs and blades exist to cut HP.
      if (opp.visible && opp.position && opp.hp !== undefined && !lowHp) {
        const oppDistance = opp.distance ?? distance(self.position, opp.position);
        if (self.bombs > 0 && oppDistance <= ARENA.bombRadius * 0.85) {
          return { action: "USE", item: "bomb", reason: "detonate on clustered enemy" };
        }
        if (self.blades > 0 && !self.bladeActive && oppDistance <= ARENA.bladeRange + 4) {
          return { action: "USE", item: "blade", reason: "spin up for close pressure" };
        }
        // Buy a bomb when we have surplus crystals and the enemy is visible.
        if (
          profile.ferocity > 0.4 &&
          self.bombs === 0 &&
          self.score >= ARENA.exchangeCost.bomb + 15 &&
          oppDistance <= 18
        ) {
          return { action: "EXCHANGE", item: "bomb", reason: "buy blast for the duel" };
        }
      }

      // 3. Caution: a valuable crystal with nothing else nearby is suspicious.
      if (profile.caution >= 0.5) {
        const suspicious = obs.resources.find(
          (r) =>
            !r.identifiedAsTrap &&
            r.value >= TRAP_BAIT_VALUE &&
            !r.inRange &&
            r.distance < 14,
        );
        if (suspicious && self.hp > 40) {
          return {
            action: "SCOUT",
            target: suspicious.position,
            reason: "high-value contact, scanning before committing",
          };
        }
      }

      // 4. Fight when it is a good trade. Ranking uses HP + score, so chip damage matters.
      if (opp.visible && opp.position && opp.hp !== undefined && !lowHp) {
        const oppDistance = opp.distance ?? distance(self.position, opp.position);
        const hpLead = self.hp - opp.hp;
        const canWin = hpLead > -30;
        const eager = profile.ferocity > 0.5;
        if ((eager || opp.hp < 80) && canWin) {
          if (oppDistance <= 3.5) {
            return { action: "ATTACK", targetId: opp.id, reason: "in melee range" };
          }
          return {
            action: "MOVE",
            target: opp.position,
            reason: eager ? "closing to engage" : "pressing a weakened enemy",
          };
        }
        if (lowHp || (opp.hp > self.hp + 40 && oppDistance < 8)) {
          return { action: "FLEE", reason: "hp critical, disengaging" };
        }
      }

      // 5. Economy: prefer combat pickups when aggressive, crystals when greedy.
      const target = bestResource(obs, (r) => {
        let w = r.value * (1 + profile.greed);
        if (r.kind === "shield") w += 8;
        if (r.kind === "bomb") w += 12 + profile.ferocity * 20;
        if (r.kind === "blade") w += 10 + profile.ferocity * 16;
        return w;
      });
      if (target) {
        const underFire = opp.visible && (opp.distance ?? 99) <= 4;
        const repeatedFailure = self.failedCollections >= 2;
        if (target.inRange) {
          if (underFire && repeatedFailure) {
            return { action: "FLEE", reason: "channel keeps breaking, disengaging" };
          }
          return { action: "COLLECT", resourceId: target.id, reason: "resource in range" };
        }
        return {
          action: "MOVE",
          target: clamp(target.position, arena),
          reason: "moving to resource",
        };
      }

      // 6. Blind. Do not freeze.
      const remembered = obs.lastKnownResources.find((r) => !r.identifiedAsTrap);
      if (remembered && distance(self.position, remembered.position) > 4) {
        return {
          action: "MOVE",
          target: clamp(remembered.position, arena),
          reason: "investigating last sighting",
        };
      }

      const sweep = sweepPoint(center, obs.tick, arena);
      if (profile.caution >= 0.5 && obs.resources.length === 0 && obs.tick % 30 < 3) {
        return { action: "SCOUT", target: sweep, reason: "sweeping for contacts" };
      }
      return { action: "MOVE", target: sweep, reason: "no intel, expanding search" };
    },
  };
}

function clamp(p: Vec2, arenaSize: number): Vec2 {
  return [
    Math.min(arenaSize - 2, Math.max(2, p[0])),
    Math.min(arenaSize - 2, Math.max(2, p[1])),
  ];
}

/**
 * A slowly rotating point around the arena centre. Deterministic in `tick`, so
 * two identical matches still produce identical movement.
 */
function sweepPoint(center: Vec2, tick: number, arenaSize: number): Vec2 {
  const angle = (tick / 60) * Math.PI * 0.5;
  const radius = arenaSize * 0.32;
  return [
    center[0] + Math.cos(angle) * radius,
    center[1] + Math.sin(angle) * radius,
  ];
}
